import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaFileSignature, FaTrash, FaExternalLinkAlt, FaUpload } from 'react-icons/fa';
import {
  updateContractSignature,
  uploadContractDocuments,
} from '../../Redux/Actions/actions';
import { RENTAL_TYPE, formatDate } from '../../Views/RentalManagement/utils/rentalTypeHelpers';
import style from './EditSignatureModal.module.css';

// Restricciones de subida (según integración backend con multer).
const MAX_FILES = 10;
const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30 MB
const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'webp'];
const FILE_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,application/pdf,image/*';

/**
 * Modal para gestionar la firma y los documentos de un contrato existente.
 * Los archivos se suben al backend con multer (multipart/form-data) mediante
 * PUT /rental/:id/documents o PUT /admin-rentals/:id/documents. Las URLs
 * resultantes quedan en `documentRent`. La firma (`hasSignedContract` /
 * `contractSignedDate`) se guarda con el endpoint de actualización del contrato.
 *
 * Props:
 * - contract: objeto de contrato normalizado (usa id, hasSignedContract, contractSignedDate, documentRent)
 * - propertyTitle?: string
 * - type?: 'regular' | 'admin'
 * - onClose: () => void
 * - onSuccess?: (result) => void
 */
const EditSignatureModal = ({
  contract = {},
  propertyTitle,
  type = RENTAL_TYPE.REGULAR,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.userId);
  const userDetails = useSelector((state) => state.userDetails);

  const [hasSignedContract, setHasSignedContract] = useState(
    Boolean(contract.hasSignedContract)
  );
  const [contractSignedDate, setContractSignedDate] = useState(
    contract.contractSignedDate
      ? String(contract.contractSignedDate).slice(0, 10)
      : ''
  );
  // Documentos ya guardados en el contrato (URLs).
  const [existingDocuments, setExistingDocuments] = useState(
    Array.isArray(contract.documentRent) ? contract.documentRent : []
  );
  // Archivos nuevos seleccionados (File), pendientes de subir al guardar.
  const [newFiles, setNewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const totalDocs = existingDocuments.length + newFiles.length;

  const handleSelectFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;

    const valid = [];
    for (const file of picked) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.warn(`Formato no permitido: ${file.name}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.warn(`El archivo supera los 30 MB: ${file.name}`);
        continue;
      }
      valid.push(file);
    }

    if (valid.length === 0) return;

    if (existingDocuments.length + newFiles.length + valid.length > MAX_FILES) {
      toast.warn(`Podés cargar hasta ${MAX_FILES} documentos por contrato.`);
      return;
    }

    setNewFiles((prev) => [...prev, ...valid]);
  };

  const removeExistingDocument = (index) => {
    setExistingDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const finalDocsCount = existingDocuments.length + newFiles.length;
    if (hasSignedContract && finalDocsCount === 0) {
      toast.warn(
        'Para marcar el contrato como firmado, cargá al menos un documento.'
      );
      return;
    }

    const meta = {
      performedBy: userId || undefined,
      performedByName: userDetails?.name || userDetails?.fullName || undefined,
    };

    try {
      setSubmitting(true);

      // 1) Si hay archivos nuevos, se suben con multer conservando los existentes.
      let finalDocuments = existingDocuments;
      if (newFiles.length > 0) {
        const updated = await dispatch(
          uploadContractDocuments(
            contract.id,
            newFiles,
            existingDocuments,
            type,
            meta
          )
        );
        finalDocuments = Array.isArray(updated?.documentRent)
          ? updated.documentRent
          : existingDocuments;
      }

      // 2) Se guarda el estado de firma + la lista final de documentos.
      const result = await dispatch(
        updateContractSignature(
          contract.id,
          {
            hasSignedContract,
            documentRent: finalDocuments,
            contractSignedDate:
              hasSignedContract && contractSignedDate ? contractSignedDate : null,
            performedBy: meta.performedBy,
            performedByName: meta.performedByName,
          },
          type
        )
      );

      // Sincronizamos el estado local con lo persistido.
      setExistingDocuments(finalDocuments);
      setNewFiles([]);
      toast.success('Documentos y estado de firma actualizados.');
      if (onSuccess) onSuccess(result);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        apiErrors.forEach((e) => toast.error(e.message || 'Error al subir documento.'));
      } else {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            'No se pudo actualizar el contrato.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting;

  const fileName = (url) => {
    try {
      const clean = String(url).split('?')[0];
      return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1));
    } catch {
      return 'Documento';
    }
  };

  return (
    <div className={style.modal} onClick={busy ? undefined : onClose}>
      <div className={style.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.modalTitle}>
          <FaFileSignature /> Gestionar firma y documentos
        </h3>
        <p className={style.subtitle}>
          {propertyTitle ? `${propertyTitle} — ` : ''}
          Cargá el contrato firmado y marcá su estado.
        </p>

        <label className={style.checkboxRow}>
          <input
            type="checkbox"
            checked={hasSignedContract}
            onChange={(e) => setHasSignedContract(e.target.checked)}
            disabled={busy}
          />
          El contrato está firmado
        </label>

        {hasSignedContract && (
          <label className={style.label}>
            Fecha de firma
            <input
              type="date"
              className={style.input}
              value={contractSignedDate}
              onChange={(e) => setContractSignedDate(e.target.value)}
              disabled={busy}
            />
          </label>
        )}

        <div className={style.docsSection}>
          <div className={style.docsHeader}>
            <span>Documentos del contrato ({totalDocs})</span>
            <label className={style.uploadBtn}>
              <FaUpload /> Agregar
              <input
                type="file"
                multiple
                accept={FILE_ACCEPT}
                style={{ display: 'none' }}
                onChange={(e) => {
                  handleSelectFiles(e.target.files);
                  e.target.value = '';
                }}
                disabled={busy || totalDocs >= MAX_FILES}
              />
            </label>
          </div>

          <p className={style.hint}>
            Hasta {MAX_FILES} archivos, 30 MB c/u. Formatos: PDF, DOC, XLS, JPG, PNG, WEBP.
          </p>

          {totalDocs === 0 ? (
            <p className={style.emptyDocs}>Sin documentos cargados.</p>
          ) : (
            <ul className={style.docsList}>
              {existingDocuments.map((url, index) => (
                <li key={`existing-${url}-${index}`} className={style.docItem}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={style.docLink}
                    title={fileName(url)}
                  >
                    <FaExternalLinkAlt /> {fileName(url)}
                  </a>
                  <button
                    type="button"
                    className={style.removeBtn}
                    onClick={() => removeExistingDocument(index)}
                    disabled={busy}
                    title="Quitar documento"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
              {newFiles.map((file, index) => (
                <li key={`new-${file.name}-${index}`} className={style.docItem}>
                  <span className={style.docLink} title={file.name}>
                    <FaUpload /> {file.name} <em className={style.pendingTag}>(pendiente)</em>
                  </span>
                  <button
                    type="button"
                    className={style.removeBtn}
                    onClick={() => removeNewFile(index)}
                    disabled={busy}
                    title="Quitar archivo"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {contract.contractSignedDate && (
          <p className={style.hint}>
            Firma registrada actualmente: {formatDate(contract.contractSignedDate)}
          </p>
        )}

        <div className={style.actions}>
          <button
            type="button"
            className={style.cancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={style.confirmBtn}
            onClick={handleSubmit}
            disabled={busy}
          >
            {submitting ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSignatureModal;
